/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps, ReactNode } from 'react'
import { ChangeEvent, forwardRef, useId } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { useFormContext, useFormState } from 'react-hook-form'
import { cn, mergeRefs } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { InputGroup, InputGroupAddon, InputGroupInput } from '../../ui/input-group'

export type InputFormFieldProps = {
  autoComplete?: ComponentProps<typeof InputGroupInput>['autoComplete']
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  forceUppercase?: boolean
  helperText?: string
  inputGroupProps?: ComponentProps<typeof InputGroup>
  inputProps?: ComponentProps<typeof InputGroupInput>
  label?: string
  labelEnd?: ReactNode
  leftElement?: ReactNode
  name: string
  placeholder?: string
  required?: boolean
  rightElement?: ReactNode
  type?: ComponentProps<typeof InputGroupInput>['type']
  validation?: RegisterOptions
}

export const InputFormField = forwardRef<HTMLInputElement, InputFormFieldProps>((props, forwardedRef) => {
  const { register, control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const { ref, ...workRegister } = register(props.name, props.validation)
  const inputId = useId()

  const { inputProps } = props
  const { ref: inputRef, className: inputClassName, onChange: inputOnChange, ...restInputProps } = inputProps ?? {}

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (props.forceUppercase) {
      e.target.value = e.target.value.toUpperCase()
    }

    inputOnChange?.(e)

    workRegister.onChange(e)
  }

  const error = errors[props.name]
  const invalid = !!error

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      {props.label !== undefined && (
        <div className={cn('flex items-center gap-1', props.labelEnd != null && 'w-full')}>
          <FieldLabel className="flex items-center gap-1" htmlFor={inputId}>
            {props.label}
            {props.required ? (
              <span aria-hidden className="text-destructive">
                *
              </span>
            ) : null}
          </FieldLabel>
          {props.labelEnd}
        </div>
      )}
      <InputGroup {...props.inputGroupProps} className={cn('flex-1', props.inputGroupProps?.className)}>
        {props.leftElement ? <InputGroupAddon align="inline-start">{props.leftElement}</InputGroupAddon> : null}
        <InputGroupInput
          suppressHydrationWarning
          {...workRegister}
          {...restInputProps}
          aria-invalid={invalid}
          autoComplete={props.autoComplete}
          className={cn(inputClassName)}
          disabled={props.disabled}
          id={inputId}
          onChange={handleChange}
          placeholder={props.placeholder}
          ref={mergeRefs(ref, forwardedRef, inputRef)}
          required={props.required}
          type={props.type}
        />
        {props.rightElement ? <InputGroupAddon align="inline-end">{props.rightElement}</InputGroupAddon> : null}
      </InputGroup>
      {props.helperText !== undefined ? (
        <FieldDescription className="text-sm">{props.helperText}</FieldDescription>
      ) : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

InputFormField.displayName = 'InputFormField'
